import React, { useEffect } from 'react';
import axios from 'axios';
import { Container, Box, Text, Flex, Card, Button } from '../../design-system';
import { useEffectOnce, useToggle } from 'react-use';
import { Form, Field } from 'react-final-form';
import { isArray } from 'lodash';
import styled, { css } from 'styled-components';
import {
  Block,
  Loadable,
  Modal,
  ModalContent,
  ModalHeader,
} from '../../design-system';
import { useData } from '../../helpers/data-loader';
import {
  errorMsg,
  formatDate,
  newformatTime,
  required,
} from '../../helpers/utils';
import Alert from '../../helpers/alert';
import {
  REPORTS,
  REPORT_OPTIONS,
  DATE_OPTIONS,
  UPCOMING_DATE_OPTIONS,
  NEEDS_DATES,
  DIRECTION_OPTIONS,
} from './constants';
import Report from './report';
import { ERR_MSG } from '../../globalConstants';
import ErrorWithDelay from '../../components/ErrorWithDelay';
import { Select } from './components';
import { formatDateRange, formValidation } from './utils';

import DateField from '../../components/DateField';

const DescriptionToggle = styled(Button)`
  top: 12px;
  outline: none;

  &:before {
    border: none;
  }
`;

const BorderSelect = styled(Select)`
  border: 1px solid #b5bac8;
  border-radius: 8px;
`;

const Error = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.error};
    font-size: 0.75rem;
    position: absolute;
    left: 0;
    bottom: -8px;
  `,
);

interface ConditionProps {
  when: string;
  is?: string | string[];
  children?: React.ReactNode;
}

const Condition = ({ when, is = [], children }: ConditionProps) => {
  const inputs = typeof is === 'string' ? [is] : is;
  return (
    <Field name={when} subscription={{ value: true }}>
      {({
        input: {
          value: { value },
        },
      }) => (inputs.some((curr) => curr === value) ? children : null)}
    </Field>
  );
};

const Reports = () => {
  const [reportData, setReportData] = React.useState<any>(null);
  const [reportOption, setReportOption] = React.useState(REPORT_OPTIONS[0]);
  const [clientFilter, setClientFilter] = React.useState<any>(null);
  const [funderFilter, setFunderFilter] = React.useState<any>(null);
  const [reportLoading, setLoading] = useToggle(false);
  const [isOpen, toggleOpen] = useToggle(false);
  const [queryExpanded, setQueryExpanded] = useToggle(true);
  const [clientData, fetchClientData] = useData('/reports/clients', {
    errorMsg: errorMsg('clients'),
  });
  const [projects, fetchProjectData] = useData('/reports/projects', {
    errorMsg: errorMsg('projects'),
  });
  const [funderData, fetchFunderData] = useData('/reports/funders', {
    errorMsg: errorMsg('funders'),
  });
  const [userData, fetchUserData] = useData('/user', {
    errorMsg: errorMsg('users'),
  });

  useEffectOnce(() => {
    fetchFunderData();
    fetchUserData();
  });

  useEffect(() => {
    fetchProjectData({ params: { clientID: clientFilter } });
  }, [clientFilter]);

  useEffect(() => {
    fetchClientData({ params: { funderID: funderFilter } });
  }, [funderFilter]);

  useEffect(() => {
    setFunderFilter(null);
  }, [reportOption]);

  const OPTIONS = {
    client: clientData.map(({ id, name }) => ({
      value: id,
      label: name,
    })),
    funder: funderData.map(({ id, name }) => ({
      value: id,
      label: name,
    })),
    user: userData.map(({ id, name }) => ({
      value: id,
      label: name,
    })),
    project: projects.map(
      ({ id, created_at, description: projectDescription }) => ({
        value: id,
        label: `${projectDescription} - ${newformatTime(created_at)}`,
      }),
    ),
  };

  const onSubmit = async (formValues: any) => {
    const payload = Object.keys(formValues).reduce((acc: any, key) => {
      const item = formValues[key];
      if (!acc[key]) {
        acc[key] = null;
      }
      if (item.value) {
        acc[key] = item.value;
      } else if (isArray(item)) {
        acc[key] = item.map(({ value }) => value);
      } else {
        acc[key] = item;
      }

      return acc;
    }, {});

    setLoading(true);
    try {
      const { data } = await axios.post(`/reports`, payload);
      const { startDate, endDate, report, ...rest } = payload;

      setReportData({
        data,
        ...rest,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        report: REPORTS.find((item) => item.value === report),
      });
      setQueryExpanded(false);
    } catch (err) {
      Alert.error(ERR_MSG);
    }
    setLoading(false);
  };

  const { start, end } = formatDateRange();
  const initialValues = {
    report: reportOption,
    dateRange: { value: 'custom_dates', label: 'Custom Dates' },
    startDate: start,
    endDate: end,
    orderBy: reportOption.sortOptions[0],
    sortDirection: DIRECTION_OPTIONS[0],
    selectFrom: { value: 'client', label: 'Clients' },
  };

  return (
    <>
      <Container fluid height="100%" p={5}>
        <Flex flexDirection="column" height="100%">
          <Box mb={4}>
            <Form
              initialValues={initialValues}
              onSubmit={onSubmit}
              validate={formValidation}
              keepDirtyOnReinitialize
            >
              {({ handleSubmit, submitting, valid, form, values }) => (
                <form onSubmit={handleSubmit}>
                  <Flex
                    mb={3}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Text fontSize={22} fontWeight={600}>
                      Download Reports
                    </Text>
                    {!queryExpanded && reportData && (
                      <Button
                        variant="grey"
                        leftIcon="edit"
                        onClick={() => setQueryExpanded(true)}
                      >
                        Edit Filters
                      </Button>
                    )}
                  </Flex>
                  {queryExpanded && (
                    <Card mb={3}>
                      <Card.Body>
                        <Flex>
                          <Box
                            flex={2}
                            mr={4}
                            style={{ borderRight: '1px solid lightgrey' }}
                          >
                            <Field name="report">
                              {({ input }) => (
                                <Flex
                                  position="relative"
                                  pb="8px"
                                  alignItems="center"
                                >
                                  <Box width="100%">
                                    <BorderSelect
                                      label="Report"
                                      options={REPORT_OPTIONS}
                                      {...input}
                                      onChange={(value) => {
                                        input.onChange(value);
                                        setReportOption(value);

                                        form.batch(() => {
                                          form.change('values', null);
                                        });
                                      }}
                                    />
                                  </Box>
                                  <DescriptionToggle
                                    leftIcon="help-circle"
                                    variant="blue"
                                    p={0}
                                    ml={2}
                                    mr={3}
                                    onClick={toggleOpen}
                                  />
                                  <ErrorWithDelay name={input.name}>
                                    {(error) => <Error>{error}</Error>}
                                  </ErrorWithDelay>
                                </Flex>
                              )}
                            </Field>
                          </Box>
                          <Condition when="report" is="opportunity_review">
                            <Field name="client">
                              {({ input }) => (
                                <Box
                                  position="relative"
                                  flex={2}
                                  pb="8px"
                                  mr={3}
                                >
                                  <BorderSelect
                                    label="Select Client"
                                    options={OPTIONS.client}
                                    {...input}
                                    onChange={(value) => {
                                      input.onChange(value);
                                      setClientFilter(value.value);
                                    }}
                                  />
                                  <ErrorWithDelay name={input.name}>
                                    {(error) => <Error>{error}</Error>}
                                  </ErrorWithDelay>
                                </Box>
                              )}
                            </Field>
                          </Condition>
                          <Condition when="report" is="funder_report">
                            <Field name="funder">
                              {({ input }) => (
                                <Box
                                  position="relative"
                                  flex={2}
                                  pb="8px"
                                  mr={3}
                                >
                                  <BorderSelect
                                    label="Select Funder"
                                    options={OPTIONS.funder}
                                    {...input}
                                    onChange={(value) => {
                                      input.onChange(value);
                                      setFunderFilter(value.value);
                                    }}
                                  />
                                  <ErrorWithDelay name={input.name}>
                                    {(error) => <Error>{error}</Error>}
                                  </ErrorWithDelay>
                                </Box>
                              )}
                            </Field>
                          </Condition>
                          <Condition
                            when="report"
                            is={[
                              'funder_report',
                              'client_awards',
                              'upcoming_projects',
                              'client_report',
                              'deadlines_report',
                            ]}
                          >
                            <Field name="values" validate={required}>
                              {({ input }) => (
                                <Box
                                  position="relative"
                                  flex={4}
                                  pb="8px"
                                  ml={3}
                                >
                                  <Flex>
                                    <Block flex={1}>
                                      <BorderSelect
                                        label="Select Clients"
                                        options={OPTIONS.client}
                                        disabled={
                                          values.report.value ===
                                            'funder_report' && !funderFilter
                                        }
                                        isDisabled={
                                          values.report.value ===
                                            'funder_report' && !funderFilter
                                        }
                                        isMulti
                                        {...input}
                                      />
                                      <ErrorWithDelay name={input.name}>
                                        {(error) => <Error>{error}</Error>}
                                      </ErrorWithDelay>
                                    </Block>
                                    <Block mt={25} ml={20}>
                                      <Button
                                        disabled={
                                          values.report.value ===
                                            'funder_report' && !funderFilter
                                        }
                                        onClick={(e) => {
                                          e.preventDefault();
                                          form.change('values', OPTIONS.client);
                                        }}
                                      >
                                        Select All
                                      </Button>
                                    </Block>
                                  </Flex>
                                </Box>
                              )}
                            </Field>
                          </Condition>
                          <Condition when="report" is="opportunity_review">
                            <Field name="values">
                              {({ input }) => (
                                <Box
                                  position="relative"
                                  flex={2}
                                  pb="8px"
                                  mr={3}
                                >
                                  <BorderSelect
                                    label="Select Project"
                                    options={OPTIONS.project}
                                    {...input}
                                    onChange={(value) => {
                                      input.onChange(value);
                                    }}
                                  />
                                  <ErrorWithDelay name={input.name}>
                                    {(error) => <Error>{error}</Error>}
                                  </ErrorWithDelay>
                                </Box>
                              )}
                            </Field>
                          </Condition>
                        </Flex>
                        <Flex mt={3}>
                          {REPORTS.map(({ value, sortOptions }) => (
                            <Condition key={value} when="report" is={value}>
                              <Field name="orderBy">
                                {({ input }) => (
                                  <Box position="relative" flex={1} pb="8px">
                                    <BorderSelect
                                      label="Sort By"
                                      options={sortOptions}
                                      {...input}
                                    />
                                    <ErrorWithDelay name={input.name}>
                                      {(error) => <Error>{error}</Error>}
                                    </ErrorWithDelay>
                                  </Box>
                                )}
                              </Field>
                              <Field name="sortDirection">
                                {({ input }) => (
                                  <Box
                                    flex={1}
                                    position="relative"
                                    pb="8px"
                                    ml={3}
                                  >
                                    <BorderSelect
                                      label="Sort Direction"
                                      options={DIRECTION_OPTIONS}
                                      {...input}
                                    />
                                    <ErrorWithDelay name={input.name}>
                                      {(error) => <Error>{error}</Error>}
                                    </ErrorWithDelay>
                                  </Box>
                                )}
                              </Field>
                            </Condition>
                          ))}
                          <Condition when="report" is={NEEDS_DATES}>
                            <Field name="dateRange">
                              {({ input }) => (
                                <Box
                                  flex={1}
                                  position="relative"
                                  pb="8px"
                                  mx={3}
                                >
                                  <BorderSelect
                                    label="Date Range"
                                    options={
                                      [
                                        'Upcoming Project Report',
                                        'Deadlines Report',
                                      ].find(
                                        (item) => item === reportOption.label,
                                      )
                                        ? UPCOMING_DATE_OPTIONS
                                        : DATE_OPTIONS
                                    }
                                    {...input}
                                    onChange={(value) => {
                                      input.onChange(value);

                                      const { start: startDate, end: endDate } =
                                        formatDateRange(value.value);

                                      form.batch(() => {
                                        form.change('startDate', startDate);
                                        form.change('endDate', endDate);
                                      });
                                    }}
                                  />
                                  <ErrorWithDelay name={input.name}>
                                    {(error) => <Error>{error}</Error>}
                                  </ErrorWithDelay>
                                </Box>
                              )}
                            </Field>
                            <Field name="startDate">
                              {({ input }) => (
                                <Box mr={3} pb="8px">
                                  <DateField
                                    label="Start Date"
                                    selected={input.value}
                                    onChange={(value) => {
                                      input.onChange(value);
                                    }}
                                  />
                                  <ErrorWithDelay name={input.name}>
                                    {(error) => <Error>{error}</Error>}
                                  </ErrorWithDelay>
                                </Box>
                              )}
                            </Field>
                            <Field name="endDate">
                              {({ input }) => (
                                <Box pb="8px">
                                  <DateField
                                    label="End Date"
                                    selected={input.value}
                                    onChange={(value) => {
                                      input.onChange(value);
                                    }}
                                  />
                                  <ErrorWithDelay name={input.name}>
                                    {(error) => <Error>{error}</Error>}
                                  </ErrorWithDelay>
                                </Box>
                              )}
                            </Field>
                          </Condition>
                        </Flex>
                      </Card.Body>
                    </Card>
                  )}
                  {queryExpanded && (
                    <Flex justifyContent="flex-end">
                      <Button
                        type="submit"
                        loading={submitting}
                        disabled={!valid}
                      >
                        Run Report
                      </Button>
                    </Flex>
                  )}
                </form>
              )}
            </Form>
          </Box>
          <Loadable loading={reportLoading}>
            {reportData && (
              <Box flex={1}>
                <Report {...reportData} />
              </Box>
            )}
          </Loadable>
        </Flex>
      </Container>
      <Modal show={isOpen} onClose={toggleOpen} style={{ maxWidth: 500 }}>
        <ModalHeader
          style={{ alignItems: 'center', fontWeight: 600, display: 'flex' }}
        >
          {reportOption.label}
        </ModalHeader>
        <ModalContent style={{ fontSize: '0.9rem' }}>
          {reportOption.description}
        </ModalContent>
      </Modal>
    </>
  );
};

export default Reports;
